/**
 * "Isto acabou de ser gravado" — o pisca-verde no lugar onde a coisa mudou.
 *
 * O retorno de toda ação da mesa era o mesmo balão preto no rodapé: clica-se
 * num botão à direita, um recado aparece embaixo por seis segundos, e a mudança
 * acontece numa linha que talvez esteja fora da vista. O olho não liga as duas
 * coisas — e é por isso que a pessoa clica de novo, e por isso que a auditoria
 * de 22/08 não conseguia mostrar num vídeo que o sistema tinha funcionado.
 *
 * Aqui não há estado global de React, e é de propósito: quem grava é
 * `estado.jsx`, quem pisca é uma linha lá no fundo de uma lista, e enfiar isso
 * no contexto faria a mesa inteira repintar a cada documento conferido. O que
 * existe é um mural minúsculo — chave → autor — e `useSyncExternalStore`, que é
 * a peça do React para exatamente isto: contar a um componente o que mudou fora
 * dele.
 *
 * A chave é do OBJETO, não da tela: `checklist:12`, `etapa:8`, `peca:3`. É o
 * que permite marcar em `estado.jsx` (que sabe o que gravou) e piscar em
 * `FichaDoCaso.jsx` (que sabe onde ele está desenhado), sem os dois se conhecerem.
 */
import { useCallback, useSyncExternalStore } from "react";

/* Quanto tempo a marca vale. 1,6s é o que dura o realce mais o selo do autor —
   passado isso a linha volta a ser uma linha, senão a tela fica piscando
   sozinha enquanto alguém lê outra coisa. */
const VALIDADE = 1600;

const mural = new Map();
const ouvintes = new Set();
const avisar = () => {
  for (const f of ouvintes) f();
};

/**
 * Alguém gravou: acende a chave, avisa quem estiver olhando, e apaga sozinho.
 *
 * Quem apaga é o relógio DAQUI, e não uma conta feita na pintura: ler o tempo
 * durante o desenho é resultado instável (o React reclama disso com razão), e o
 * componente não tem por que saber quanto dura um realce.
 */
export function gravou(chave, autor) {
  if (!chave) return;
  const anterior = mural.get(chave);
  if (anterior?.relogio) clearTimeout(anterior.relogio);
  const relogio = setTimeout(() => {
    mural.delete(chave);
    avisar();
  }, VALIDADE);
  mural.set(chave, { autor: autor || "", relogio });
  avisar();
}

const NADA = { aceso: false, autor: "" };
/* O valor devolvido precisa ser ESTÁVEL entre leituras — objeto novo a cada
   chamada faz `useSyncExternalStore` repintar para sempre. Por isso a marca
   guardada no mural é a própria resposta, e a ausência é sempre `NADA`. */
const lerMarca = (chave) => (chave && mural.get(chave)) || NADA;

/**
 * A chave está acesa agora? — a mesma pergunta do gancho, sem React.
 *
 * Existe porque a parte que precisa de teste é o RELÓGIO: que a marca apaga
 * sozinha. Montar componente para conferir isso seria testar o React; o que
 * envelhece aqui é o prazo.
 */
export const estaAceso = (chave) => lerMarca(chave) !== NADA;

/**
 * `{ aceso, autor }` para a chave — `aceso` volta a `false` sozinho.
 *
 * Devolve o autor junto porque o selo diz "por quem": é o mesmo nome que já vai
 * para `events`, e é a diferença entre "mudou" e "a Camila mudou".
 */
export function useGravou(chave) {
  const assinar = useCallback((aoMudar) => {
    ouvintes.add(aoMudar);
    return () => ouvintes.delete(aoMudar);
  }, []);
  const ler = useCallback(() => lerMarca(chave), [chave]);
  /* O terceiro argumento é a leitura do servidor: na renderização de servidor
     não há nada aceso, e é o que se quer — o realce é de quem acabou de
     clicar. */
  const marca = useSyncExternalStore(assinar, ler, () => NADA);
  return { aceso: marca !== NADA, autor: marca.autor ?? "" };
}
